import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import {
  Banner,
  Button,
  Flex,
  Heading,
  Text,
  toast,
  UploadIcon,
} from '@buffer-mono/popcorn'

import { FileInput } from '~publish/components/FileInput'
import * as UploadDropzone from '~publish/components/UploadDropzone'
import { TimeZoneSelect } from '~publish/components/TimeZoneSelect'
import { useAccountTimezone } from '~publish/contexts/AccountContext'
import { parseCsvFile } from '~publish/helpers/csv/parseCsvFile'

import type { ImportItem, MigrationSource } from '../MigrationWizard/types'
import {
  extractSourceChannels,
  isHootsuiteFormat,
  parseHootsuiteRow,
} from './hootsuite'

type CsvUploadStepProps = {
  source: MigrationSource
  onParsed: (items: ImportItem[], timezone: string) => void
}

const MAX_ROWS = 5000

export function CsvUploadStep({
  source,
  onParsed,
}: CsvUploadStepProps): JSX.Element {
  const accountTimezone = useAccountTimezone()
  const [timezone, setTimezone] = useState(accountTimezone)
  const [parseResult, setParseResult] = useState<{
    items: ImportItem[]
    totalRows: number
    errorCount: number
    channelCount: number
    hasMedia: boolean
  } | null>(null)

  const handleFile = useCallback(
    (file: File) => {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file.')
        return
      }

      void parseCsvFile<Record<string, string>>(file)
        .then(({ data, headers }) => {
          if (source === 'hootsuite' && !isHootsuiteFormat(headers)) {
            toast.error(
              "This doesn't look like a Hootsuite export. Expected columns: Social Account, Account Name, Scheduled Time.",
            )
            return
          }

          if (data.length === 0) {
            toast.error('The CSV file is empty.')
            return
          }

          if (data.length > MAX_ROWS) {
            toast.error(
              `The CSV file has ${data.length.toLocaleString()} rows, which exceeds the maximum of ${MAX_ROWS.toLocaleString()}. Please split the file and import in batches.`,
            )
            return
          }

          const items = data.map((row, index) => parseHootsuiteRow(row, index))

          const errorCount = items.filter((i) => i.status === 'error').length
          const channels = extractSourceChannels(items)

          const hasMedia = items.some((i) => i.post.media)

          setParseResult({
            items,
            totalRows: items.length,
            errorCount,
            channelCount: channels.size,
            hasMedia,
          })
          return undefined
        })
        .catch(() => {
          toast.error(
            'Could not parse the CSV file. Please check the file format.',
          )
        })
    },
    [source],
  )

  const handleDrop = useCallback(
    (files: File[]) => {
      const file = files[0]
      if (file) handleFile(file)
    },
    [handleFile],
  )

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) handleFile(file)
    },
    [handleFile],
  )

  const handleContinue = (): void => {
    if (parseResult) {
      onParsed(parseResult.items, timezone)
    }
  }

  return (
    <Flex direction="column" gap="lg" align="center">
      {!parseResult ? (
        <UploadDropzone.Root onDrop={handleDrop}>
          <Flex
            direction="column"
            align="center"
            gap="sm"
            style={{ padding: 'var(--space-2xl) var(--space-lg)' }}
          >
            <UploadIcon size="large" />
            <Heading size="small">Drop your CSV file here</Heading>
            <Text color="subtle">or click below to select a file</Text>
            <FileInput accept=".csv" onChange={handleFileChange}>
              <Button variant="secondary">Choose CSV File</Button>
            </FileInput>
          </Flex>
        </UploadDropzone.Root>
      ) : (
        <>
          <Banner variant="info">
            <Banner.Text>
              Parsed {parseResult.totalRows.toLocaleString()} posts across{' '}
              {parseResult.channelCount} channel
              {parseResult.channelCount !== 1 ? 's' : ''}.
              {parseResult.errorCount > 0 && (
                <>
                  {' '}
                  {parseResult.errorCount} row
                  {parseResult.errorCount !== 1 ? 's' : ''} had issues and will
                  be skipped.
                </>
              )}
            </Banner.Text>
          </Banner>

          {parseResult.hasMedia && (
            <Banner variant="warning">
              <Banner.Text>
                Some posts contain media. Media URLs from Hootsuite expire
                approximately 1 week after export. If your export is older,
                images may fail to import and those posts will be created as
                text-only.
              </Banner.Text>
            </Banner>
          )}

          <Flex direction="column" gap="xs">
            <Text weight="bold">Timezone</Text>
            <Text color="subtle" size="sm">
              Confirm the timezone your Hootsuite posts were scheduled in.
            </Text>
            <TimeZoneSelect value={timezone} onChange={setTimezone} />
          </Flex>

          <Flex gap="sm" justify="end">
            <Button variant="secondary" onClick={() => setParseResult(null)}>
              Upload different file
            </Button>
            <Button variant="primary" onClick={handleContinue}>
              Continue to Channel Mapping
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  )
}
